import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, ChevronRight, FileText, Loader2, Sparkles, X } from 'lucide-react';
import React, { useState } from 'react';

import { InspectArtifactButton } from '@/components/artifact-inspector/InspectArtifactButton';
import { Button } from '@/components/ui/button';
import type { ReviewFoundProviderEntry } from '@/services/types/treatment-types';

interface ReviewProviderCardProps {
  provider: ReviewFoundProviderEntry;
  onApprove: (providerId: string) => void;
  onReject: (providerId: string) => void;
  isApproving: boolean;
  isRejecting: boolean;
}

export const ReviewProviderCard: React.FC<ReviewProviderCardProps> = ({
  provider,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}) => {
  const [expanded, setExpanded] = useState(false);
  const mention = provider.latest_mention;

  return (
    <div className="rounded-lg border border-amber-200/70 bg-amber-50/50">
      {/* Compact row */}
      <div
        role="button"
        tabIndex={0}
        className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5"
        onClick={() => setExpanded((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
      >
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-400" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">
          {provider.name || 'Unknown Provider'}
        </span>
        {mention?.specialty_hint && (
          <span className="shrink-0 rounded-full bg-amber-100/70 px-2 py-0.5 text-[10px] font-medium text-amber-700">
            {mention.specialty_hint}
          </span>
        )}
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onApprove(provider.medical_provider_id);
            }}
            disabled={isApproving || isRejecting}
            className="h-6 w-6 p-0 text-green-700 hover:bg-green-100 hover:text-green-800"
            title="Approve provider"
          >
            {isApproving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onReject(provider.medical_provider_id);
            }}
            disabled={isApproving || isRejecting}
            className="h-6 w-6 p-0 text-red-700 hover:bg-red-100 hover:text-red-800"
            title="Reject provider"
          >
            {isRejecting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            title={expanded ? 'Collapse details' : 'Expand details'}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>

      {/* Expanded evidence */}
      <AnimatePresence initial={false}>
        {expanded && mention && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="space-y-2 border-t border-amber-200/70 px-3 pb-3 pt-2">
              {/* Location hint */}
              {mention.location_hint && (
                <p className="text-xs font-medium text-gray-600">
                  <span className="mr-1">📍</span>
                  {mention.location_hint}
                </p>
              )}

              {/* Source document */}
              {mention.artifact_id && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div
                  className="flex items-center gap-1.5 rounded bg-blue-50 px-2 py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FileText className="h-3 w-3 shrink-0 text-blue-600" />
                  <span className="text-[10px] font-medium text-blue-700">
                    Source: {mention.artifact_type.replace(/_/g, ' ')}
                  </span>
                  <InspectArtifactButton
                    artifactId={mention.artifact_id}
                    artifactType={mention.artifact_type}
                    compact
                    buttonClassName="ml-auto p-0.5 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded transition-colors"
                  />
                </div>
              )}

              {/* Excerpts */}
              {mention.excerpts && mention.excerpts.length > 0 && (
                <div className="space-y-0.5">
                  {mention.excerpts.slice(0, 3).map((excerpt, i) => (
                    <p
                      key={i}
                      className="border-l-2 border-amber-300 pl-2 text-xs italic text-gray-600"
                    >
                      &ldquo;{excerpt}&rdquo;
                    </p>
                  ))}
                </div>
              )}

              {/* AI reasoning */}
              {mention.reasoning && (
                <p className="line-clamp-3 text-xs text-gray-600">
                  <span className="font-medium text-gray-700">AI: </span>
                  {mention.reasoning}
                </p>
              )}

              {/* Confidence bar */}
              {mention.confidence != null && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-gray-500">Confidence:</span>
                  <div className="h-1.5 flex-1 rounded-full bg-amber-100">
                    <div
                      className="h-full rounded-full bg-amber-400"
                      style={{ width: `${Math.round(mention.confidence * 100)}%` }}
                    />
                  </div>
                  <span className="text-[10px] font-medium text-gray-600">
                    {Math.round(mention.confidence * 100)}%
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
